/*
  "The Lineup" flavor carousel (sections/highrise-home-flavor-stack.liquid).

  Desktop (>780px): unchanged — the track is a plain `overflow-x: auto;
  scroll-snap-type: x mandatory` element (see highrise-nothing.css),
  which every browser already scrolls on finger/trackpad swipe. This
  file just wires up the prev/next arrows and the filter pills there.

  Mobile (<=780px): a 3D "coverflow" instead — one card centered and at
  full color/scale, its neighbors turned edge-on, scaled down and
  grayed out behind it. There's no CSS-only way to express "rotate
  toward whichever card is currently active," so this file owns the
  per-card transform math directly (see positionItems below) and
  toggles a `--coverflow` class that highrise-nothing.css gates its
  absolute-positioning rules behind — the two breakpoints (this file's
  MOBILE_QUERY, that file's `@media (max-width: 780px)`) are kept at
  the same value on purpose so they never disagree about which mode is
  live.
*/
(function () {
  var MOBILE_QUERY = '(max-width: 780px)';

  function initCarousel(root) {
    var track = root.querySelector('[data-flavor-track]');
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-flavor-card]'));
    var pills = Array.prototype.slice.call(root.querySelectorAll('[data-flavor-filter]'));
    var prevBtn = root.querySelector('[data-flavor-prev]');
    var nextBtn = root.querySelector('[data-flavor-next]');
    var dotsWrap = root.querySelector('[data-flavor-dots]');
    if (!track || items.length === 0) return;

    var mq = window.matchMedia(MOBILE_QUERY);
    var activeIndex = 0;

    function visibleItems() {
      return items.filter(function (item) {
        return !item.hidden;
      });
    }

    /* ---------------------------------------------------------- */
    /* Desktop scroll-snap fit (unchanged behavior)                 */
    /* ---------------------------------------------------------- */

    function updateFit() {
      var fits = track.scrollWidth <= track.clientWidth + 1;
      track.classList.toggle('hn-flavor-carousel__track--fits', fits);
    }

    function scrollByCard(direction) {
      var visible = visibleItems();
      if (!visible.length) return;
      var cardWidth = visible[0].getBoundingClientRect().width;
      var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
      track.scrollBy({ left: direction * (cardWidth + gap), behavior: 'smooth' });
    }

    /* ---------------------------------------------------------- */
    /* Mobile coverflow                                             */
    /* ---------------------------------------------------------- */

    function buildDots() {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      visibleItems().forEach(function (item, i) {
        var name = item.querySelector('.hn-flavor-cards__name');
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'hn-flavor-carousel__dot';
        button.setAttribute('role', 'tab');
        button.setAttribute(
          'aria-label',
          name ? name.textContent.trim() : 'Flavor ' + (i + 1)
        );
        button.addEventListener('click', function () {
          setActiveIndex(i);
        });
        dotsWrap.appendChild(button);
      });
    }

    function updateDots() {
      if (!dotsWrap) return;
      var dots = dotsWrap.querySelectorAll('.hn-flavor-carousel__dot');
      dots.forEach(function (dot, i) {
        var isActive = i === activeIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', String(isActive));
      });
    }

    function positionItems() {
      if (!mq.matches) return;
      // Belt-and-suspenders: positionItems() is what actually depends on
      // this class (see the CSS it gates in highrise-nothing.css), so it
      // sets it itself rather than trusting every caller to have already
      // done so — applyFilter() below used to call this without ever
      // toggling the class, leaving cards with coverflow's inline
      // transforms applied on top of the *desktop* flex-row layout the
      // class alone switches away from (each item its own absolutely-
      // positioned slot) — a real bug, confirmed live: cards rendered
      // squished/offset inside their normal flex track box instead of
      // stacked as one centered card with graying neighbors.
      track.classList.add('hn-flavor-carousel__track--coverflow');
      var visible = visibleItems();
      if (!visible.length) return;
      if (activeIndex >= visible.length) activeIndex = visible.length - 1;
      if (activeIndex < 0) activeIndex = 0;

      var slotWidth = track.clientWidth * 0.4;
      var tallest = 0;

      visible.forEach(function (item, i) {
        var d = i - activeIndex;
        var absD = Math.abs(d);
        var isActive = d === 0;
        var scale = isActive ? 1 : 0.82;
        var rotate = isActive ? 0 : d > 0 ? -22 : 22;
        var translateX = d * slotWidth;
        var opacity = absD === 0 ? 1 : absD === 1 ? 0.55 : 0;

        item.style.transform =
          'translateX(-50%) translateX(' + translateX + 'px) scale(' + scale + ') rotateY(' + rotate + 'deg)';
        item.style.opacity = String(opacity);
        item.style.zIndex = String(10 - absD);
        item.classList.toggle('is-flavor-active', isActive);
        item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        /* `inert` (not just pointer-events) so a preorder card's own
           nested email field/submit button can't be tabbed into or
           submitted while it's a grayed-out neighbor, not just visually
           unclickable. */
        item.inert = absD > 1 || !isActive;

        if (absD <= 1) tallest = Math.max(tallest, item.offsetHeight);
      });

      track.style.height = tallest ? tallest + 'px' : '';
      updateDots();
    }

    function setActiveIndex(index) {
      var visible = visibleItems();
      if (!visible.length) return;
      var count = visible.length;
      activeIndex = ((index % count) + count) % count; // wrap both directions
      positionItems();
    }

    function clearCoverflowStyles() {
      items.forEach(function (item) {
        item.style.transform = '';
        item.style.opacity = '';
        item.style.zIndex = '';
        item.classList.remove('is-flavor-active');
        item.removeAttribute('aria-hidden');
        item.inert = false;
      });
      track.style.height = '';
    }

    function setMode() {
      var isMobile = mq.matches;
      track.classList.toggle('hn-flavor-carousel__track--coverflow', isMobile);
      if (isMobile) {
        activeIndex = 0;
        buildDots();
        positionItems();
      } else {
        clearCoverflowStyles();
        updateFit();
      }
      if (nextBtn) nextBtn.hidden = isMobile && visibleItems().length <= 1;
      if (prevBtn) prevBtn.hidden = isMobile && visibleItems().length <= 1;
      if (dotsWrap) dotsWrap.hidden = !isMobile || visibleItems().length <= 1;
    }

    /* ---------------------------------------------------------- */
    /* Filter pills                                                 */
    /* ---------------------------------------------------------- */

    function applyFilter(group) {
      items.forEach(function (item) {
        item.hidden = item.dataset.group !== group;
      });
      pills.forEach(function (pill) {
        var isActive = pill.dataset.flavorFilter === group;
        pill.classList.toggle('is-active', isActive);
        pill.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
      if (mq.matches) {
        activeIndex = 0;
        buildDots();
        positionItems();
        if (nextBtn) nextBtn.hidden = visibleItems().length <= 1;
        if (prevBtn) prevBtn.hidden = visibleItems().length <= 1;
        if (dotsWrap) dotsWrap.hidden = visibleItems().length <= 1;
      } else {
        track.scrollTo({ left: 0, behavior: 'auto' });
        updateFit();
      }
    }

    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        applyFilter(pill.dataset.flavorFilter);
      });
    });

    /* ---------------------------------------------------------- */
    /* Nav arrows — scroll on desktop, rotate the stack on mobile   */
    /* ---------------------------------------------------------- */

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        if (mq.matches) {
          setActiveIndex(activeIndex - 1);
        } else {
          scrollByCard(-1);
        }
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        if (mq.matches) {
          setActiveIndex(activeIndex + 1);
        } else {
          scrollByCard(1);
        }
      });
    }

    /* ---------------------------------------------------------- */
    /* Tap a grayed-out neighbor to bring it to the front            */
    /* ---------------------------------------------------------- */

    items.forEach(function (item, i) {
      item.addEventListener('click', function (event) {
        if (!mq.matches) return;
        var visible = visibleItems();
        var visibleIndex = visible.indexOf(item);
        if (visibleIndex === -1 || visibleIndex === activeIndex) return;
        event.preventDefault();
        setActiveIndex(visibleIndex);
      });
    });

    /* ---------------------------------------------------------- */
    /* Swipe — Pointer Events cover touch, mouse, and pen in one     */
    /* code path. A simple threshold-on-release check (not a live    */
    /* drag-follow) — enough to feel like a real swipe without the   */
    /* extra complexity of tracking/animating an in-progress drag.   */
    /*                                                               */
    /* Needs touch-action: pan-y on the track/items (see              */
    /* highrise-nothing.css) to actually receive a pointerup at all — */
    /* without it the browser treats a horizontal drag as an          */
    /* ambiguous native pan attempt and fires pointercancel instead,   */
    /* which is why swiping did nothing while the arrows (plain taps, */
    /* no drag ambiguity) kept working.                                */
    /* ---------------------------------------------------------- */

    var pointerStartX = null;
    var pointerId = null;

    track.addEventListener('pointerdown', function (event) {
      if (!mq.matches) return;
      pointerStartX = event.clientX;
      pointerId = event.pointerId;
      // Keeps this same pointer's move/up events targeted at the track
      // even as the active card scales/rotates out from under the
      // finger mid-drag — without capture, a touch that drifts onto a
      // neighboring (differently-transformed) card can otherwise end
      // up misattributed. Wrapped defensively: setPointerCapture can
      // throw if the pointer's already gone (e.g. a very quick tap).
      if (track.setPointerCapture) {
        try {
          track.setPointerCapture(event.pointerId);
        } catch (e) {}
      }
    });
    track.addEventListener('pointerup', function (event) {
      if (!mq.matches || pointerStartX === null) return;
      var deltaX = event.clientX - pointerStartX;
      pointerStartX = null;
      if (track.releasePointerCapture && pointerId !== null) {
        try {
          track.releasePointerCapture(pointerId);
        } catch (e) {}
      }
      pointerId = null;
      var SWIPE_THRESHOLD = 40;
      if (deltaX > SWIPE_THRESHOLD) {
        setActiveIndex(activeIndex - 1);
      } else if (deltaX < -SWIPE_THRESHOLD) {
        setActiveIndex(activeIndex + 1);
      }
    });
    track.addEventListener('pointercancel', function () {
      pointerStartX = null;
      pointerId = null;
    });

    /* ---------------------------------------------------------- */
    /* Keyboard — arrow keys move the stack while focus is inside    */
    /* the carousel, same as the visible prev/next buttons.          */
    /* ---------------------------------------------------------- */

    track.addEventListener('keydown', function (event) {
      if (!mq.matches) return;
      if (event.key === 'ArrowLeft') {
        setActiveIndex(activeIndex - 1);
        event.preventDefault();
      } else if (event.key === 'ArrowRight') {
        setActiveIndex(activeIndex + 1);
        event.preventDefault();
      }
    });

    /* ---------------------------------------------------------- */
    /* Init + responsive mode switching                              */
    /* ---------------------------------------------------------- */

    if (pills.length) {
      applyFilter(pills[0].dataset.flavorFilter);
    } else {
      setMode();
    }

    if (mq.addEventListener) {
      mq.addEventListener('change', setMode);
    } else if (mq.addListener) {
      // Safari < 14
      mq.addListener(setMode);
    }

    var resizeRaf = null;
    window.addEventListener('resize', function () {
      if (!mq.matches) return;
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(positionItems);
    });
  }

  function init() {
    document.querySelectorAll('[data-flavor-carousel]').forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
